import type { ReactNode } from 'react'
import moment from 'moment'
import Group from 'components/utils/Group'
import type { FlashCancellationPolicyView } from './getFlashCancellationPolicyView'
import OfferPropertyCancellationPolicies from './OfferPropertyCancellationPolicies'
import OfferPropertyFlexibleCancellationPoliciesView from './OfferPropertyFlexibleCancellationPoliciesView'
import OfferPropertyNonRefundablePolicy from './OfferPropertyNonRefundablePolicy'

export interface FlashCancellationPoliciesViewProps {
  policyView: FlashCancellationPolicyView
  propertyTimezoneOffset?: number
  isFlashUSP?: boolean
  /**
   * Optional leading affordance (e.g. shield). Only rendered when at least one policy
   * branch is visible — avoids orphan icons while charter dates resolve or windows expire.
   */
  leadingIcon?: ReactNode
}

/**
 * Presentational Flash cancellation gates. No Redux — callers pass a resolved policyView.
 */
function FlashCancellationPoliciesView(props: FlashCancellationPoliciesViewProps) {
  const {
    policyView,
    propertyTimezoneOffset,
    isFlashUSP,
    leadingIcon,
  } = props

  // Live wall-clock check (same predicate as OfferPropertyCancellationPolicies) so memoized
  // policyView eligibility cannot leave a leadingIcon up after the free-cancel window expires.
  const showFreeCancellation = policyView.showFreeCancellation
    && !policyView.freeCancellationStartDate?.isBefore(moment())
  const showFlexibleCancellation = !!policyView.flexibleTimelineContext
  const showNonRefundable = policyView.showNonRefundable
  const hasVisiblePolicy = showFreeCancellation || showFlexibleCancellation || showNonRefundable

  if (!hasVisiblePolicy) {
    return null
  }

  const content = (
    <>
      {showFreeCancellation && <OfferPropertyCancellationPolicies
        propertyTimezoneOffset={propertyTimezoneOffset}
        checkInDate={policyView.policyReferenceDate}
        isFlashUSP={isFlashUSP}
      />}
      {showFlexibleCancellation && policyView.flexibleTimelineContext && <OfferPropertyFlexibleCancellationPoliciesView
        details={policyView.flexibleTimelineContext.details}
        isCharter={policyView.flexibleTimelineContext.isCharter}
        isFlashUSP={isFlashUSP}
      />}
      {showNonRefundable && <OfferPropertyNonRefundablePolicy />}
    </>
  )

  if (!leadingIcon) {
    return content
  }

  return (
    <Group direction="horizontal" gap={8} verticalAlign="start">
      {leadingIcon}
      {content}
    </Group>
  )
}

export default FlashCancellationPoliciesView
